import { useEffect, useState, useCallback } from 'react';
import {
  Box, Stack, Typography, Input, Button,
  Modal, ModalDialog, ModalClose,
  FormControl, FormLabel, Select, Option, Textarea,
} from '@mui/joy';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { APIFinanceHealth, APIFinanceManualInputs, APIFinanceManualInputSave } from '../../../api';
import {
  useFinanceToken, useIsMobile, PeriodBar, MetricCard, MetricGrid, SectionCard, fmtMoney,
  todayISO, monthStartISO, LIGHT_COLORS,
} from './financeShared';

// Форматирование значения метрики по единице измерения (unit приходит с бэка)
const fmtMetric = (m) => {
  if (m.value === null || m.value === undefined) return '—';
  if (m.unit === '₽') return fmtMoney(m.value);
  if (m.unit === '%') return `${Number(m.value).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`;
  if (m.unit === 'дн.') return `${Number(m.value).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} дн.`;
  return Number(m.value).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
};

const TREND_OPTIONS = [
  { key: 'margin',                   label: 'Рентабельность, %' },
  { key: 'dso_days',                 label: 'DSO, дней' },
  { key: 'cashflow_net',             label: 'Сальдо ДДС, ₽' },
  { key: 'current_ratio',            label: 'Текущая ликвидность' },
  { key: 'overdue_receivable_share', label: 'Просроч. дебиторка, %' },
];

export default function FinanceHealthTab() {
  const token = useFinanceToken();
  const isMobile = useIsMobile();
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trendKey, setTrendKey] = useState('margin');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    APIFinanceHealth({ date_from: dateFrom, date_to: dateTo, trend_months: 6 }, token)
      .then(setData)
      .catch((e) => console.error('health load', e))
      .finally(() => setLoading(false));
  }, [token, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const trendData = (data?.trend || []).map((t) => ({ period: t.period, value: t[trendKey] ?? 0 }));

  return (
    <Box>
      {/* Период + ручной ввод */}
      <PeriodBar
        from={dateFrom} to={dateTo}
        onFrom={setDateFrom} onTo={setDateTo}
        onPreset={(f, t) => { setDateFrom(f); setDateTo(t); }}
        loading={loading}
        right={
          <Button size="sm" variant="outlined" color="neutral" onClick={() => setModalOpen(true)} sx={{ borderRadius: 'lg' }}>
            Данные баланса
          </Button>
        }
      />

      {/* 5 метрик со светофорами */}
      <MetricGrid>
        {(data?.metrics || []).map((m, i) => (
          <MetricCard key={m.key} index={i} title={m.title} value={fmtMetric(m)} light={m.light}
            color={LIGHT_COLORS[m.light] || '#6366f1'} />
        ))}
      </MetricGrid>

      {/* Балансы — контекст для ликвидности */}
      <SectionCard title="Баланс (контекст ликвидности)" sx={{ bgcolor: 'background.level1' }}>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Typography level="body-sm">Деньги на счетах: <b>{fmtMoney(data?.balances?.cash)}</b></Typography>
          <Typography level="body-sm">Дебиторка: <b>{fmtMoney(data?.balances?.receivable)}</b></Typography>
          <Typography level="body-sm">из них просрочено: <b>{fmtMoney(data?.balances?.receivable_overdue)}</b></Typography>
          <Typography level="body-sm">Кредиторка: <b>{fmtMoney(data?.balances?.payable)}</b></Typography>
        </Stack>
      </SectionCard>

      {/* Тренд по месяцам */}
      <SectionCard
        title="Тренд по месяцам"
        action={
          <Select size="sm" value={trendKey} onChange={(_, v) => v && setTrendKey(v)} sx={{ minWidth: 220 }}>
            {TREND_OPTIONS.map((o) => <Option key={o.key} value={o.key}>{o.label}</Option>)}
          </Select>
        }
      >
        {trendData.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.tertiary', py: 3, textAlign: 'center' }}>
            Пока нет снимков за прошлые месяцы — появятся после запуска команды snapshot_health_metrics.
          </Typography>
        ) : (
          <Box sx={{ height: { xs: 260, md: 300 }, mx: { xs: -0.5, md: 0 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 8, right: 12, left: isMobile ? -18 : 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="healthTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={isMobile ? 46 : 64} tickFormatter={(v) => Number(v).toLocaleString('ru-RU')} />
                <Tooltip formatter={(v) => Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
                  contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} fill="url(#healthTrend)" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </SectionCard>

      <ManualBalanceModal
        open={modalOpen}
        token={token}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); load(); }}
      />
    </Box>
  );
}

// ── Модалка ручного ввода данных баланса ──
function ManualBalanceModal({ open, onClose, token, onSaved }) {
  const isMobile = useIsMobile();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [equity, setEquity] = useState('');
  const [ltl, setLtl] = useState('');
  const [cash, setCash] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  // Подтягиваем существующую запись за выбранный (год, месяц)
  useEffect(() => {
    if (!open || !token) return;
    APIFinanceManualInputs(token).then((list) => {
      const rec = (list || []).find((r) => r.year === Number(year) && r.month === Number(month));
      setEquity(rec?.equity ?? '');
      setLtl(rec?.long_term_liabilities ?? '');
      setCash(rec?.cash_override ?? '');
      setComment(rec?.comment ?? '');
    }).catch(() => {});
  }, [open, token, year, month]);

  const save = () => {
    setSaving(true);
    APIFinanceManualInputSave({
      year: Number(year),
      month: Number(month),
      equity: equity === '' ? 0 : Number(equity),
      long_term_liabilities: ltl === '' ? 0 : Number(ltl),
      cash_override: cash === '' ? null : Number(cash),
      comment,
    }, token)
      .then(() => onSaved())
      .catch((e) => console.error('manual save', e))
      .finally(() => setSaving(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          sx={{
            width: { xs: '100%', sm: 440 }, borderRadius: { xs: 0, sm: 'xl' },
            maxHeight: { xs: '100%', sm: '90dvh' }, overflowY: 'auto',
          }}
        >
          <ModalClose onClick={onClose} />
          <Typography level="title-md" sx={{ mb: 0.5 }}>Данные баланса (ручной ввод)</Typography>
          <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Заполняется по месяцам. «Остаток денег» влияет на метрику «Текущая ликвидность»
            (если пусто — считается по журналу ДДС). Капитал и долгосрочные обязательства
            хранятся для отчётности.
          </Typography>
          <Stack spacing={1.2}>
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Год</FormLabel>
                <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Месяц</FormLabel>
                <Select value={Number(month)} onChange={(_, v) => v && setMonth(v)}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <Option key={m} value={m}>{m}</Option>)}
                </Select>
              </FormControl>
            </Stack>
            <FormControl>
              <FormLabel>Собственный капитал, ₽</FormLabel>
              <Input type="number" value={equity} onChange={(e) => setEquity(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Долгосрочные обязательства, ₽</FormLabel>
              <Input type="number" value={ltl} onChange={(e) => setLtl(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Остаток денег на счетах, ₽ (необязательно)</FormLabel>
              <Input type="number" value={cash} onChange={(e) => setCash(e.target.value)}
                placeholder="если пусто — считается по журналу ДДС" />
            </FormControl>
            <FormControl>
              <FormLabel>Комментарий</FormLabel>
              <Textarea minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
            </FormControl>
            <Button onClick={save} loading={saving} sx={{ mt: 0.5 }}>Сохранить</Button>
          </Stack>
        </ModalDialog>
    </Modal>
  );
}